/**
 * Teams read-only map viewer — public share token (PRD §5.5)
 */
import React, { useEffect, useState } from 'react';
import { officeService } from '../../../services/api/officeService';
import { Spiner } from '../../../components';
import './TeamsEmbed.css';

interface SharedMap {
    id: string;
    title: string;
    description?: string;
    ownerName?: string;
    stepCount?: number;
    updatedAt?: string;
    editUrl?: string;
    readonly?: boolean;
}

const TeamsMapViewer: React.FC = () => {
    const params = new URLSearchParams(window.location.search);
    const processId = params.get('processId') || '';
    const token = params.get('token') || '';

    const [map, setMap] = useState<SharedMap | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!token) {
                setError('Missing share token');
                setLoading(false);
                return;
            }
            try {
                const data = await officeService.getSharedMap(token);
                if (!cancelled) {
                    setMap(data as SharedMap);
                }
            } catch (err: any) {
                if (!cancelled) {
                    setError(err?.message || 'Unable to load shared map');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    if (loading) {
        return (
            <div className="teams-embed teams-embed--viewer">
                <Spiner className="d-flex justify-content-center spinner-border-sm" />
            </div>
        );
    }

    if (error || !map) {
        return (
            <div className="teams-embed teams-embed--viewer">
                <p className="teams-embed__error">{error || 'Map not found'}</p>
            </div>
        );
    }

    return (
        <div className="teams-embed teams-embed--viewer">
            <header className="teams-embed__viewer-bar">
                <div>
                    <h1>{map.title}</h1>
                    <p>
                        {map.ownerName || '—'}
                        {typeof map.stepCount === 'number' ? ` · ${map.stepCount} steps` : ''}
                        {map.updatedAt ? ` · updated ${map.updatedAt.slice(0, 10)}` : ''}
                    </p>
                </div>
                <a
                    className="teams-embed__cta"
                    href={map.editUrl || `/process/mapping/${processId || map.id}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    Edit in Famla
                </a>
            </header>

            <div className="teams-embed__readonly-panel">
                <p className="teams-embed__badge">Read-only in Teams</p>
                {map.description ? (
                    <div className="teams-embed__description">{map.description}</div>
                ) : (
                    <p className="teams-embed__empty">
                        No description yet. Open Famla to view the full swimlane and edit the map.
                    </p>
                )}
            </div>
        </div>
    );
};

export default TeamsMapViewer;
